import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { desktopPathToMobile, mobileAdmin, shouldRedirectUnmappedDesktopPath } from '@/lib/mobileAdminPaths';
import { apiFetch } from '@/lib/api';
import { getLoginPath } from '@/lib/loginPath';
import { cn } from '@/lib/cn';
import { useSession } from '@/hooks/useSession';
import { MobileTabBar } from '@/components/MobileTabBar';
import { HeaderAccountBar } from '@/components/HeaderAccountBar';
import { SiteScopeBar } from '@/components/SiteScopeBar';
import { APP_NAV_SECTIONS } from '@/config/appNav';
import { isSiteScopedPath } from '@/lib/siteScopedRoutes';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { APP_BRAND_TITLE } from '@/lib/appVersion';
import { MediaStorageProvider } from '@/components/MediaStorageProvider';

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const { ok, refresh, currentSiteId, capabilities } = useSession();
  const siteScoped = isSiteScopedPath(location.pathname);

  useEffect(() => {
    const onRefresh = () => {
      void refresh();
    };
    window.addEventListener('wes-session-refresh', onRefresh);
    return () => window.removeEventListener('wes-session-refresh', onRefresh);
  }, [refresh]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /** 窄屏统一进入 /m/* 手机后台，避免沿用桌面侧栏布局 */
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    function redirectMobile() {
      if (!mq.matches) return;
      const p = location.pathname;
      if (p.startsWith('/m')) return;
      const suffix = `${location.search}${location.hash}`;
      const target = desktopPathToMobile(p);
      if (target) {
        navigate(`${target}${suffix}`, { replace: true });
        return;
      }
      if (shouldRedirectUnmappedDesktopPath(p)) {
        navigate(`${mobileAdmin.dashboard}${suffix}`, { replace: true });
      }
    }
    redirectMobile();
    mq.addEventListener('change', redirectMobile);
    return () => mq.removeEventListener('change', redirectMobile);
  }, [location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  async function logout() {
    await apiFetch('logout', { method: 'POST', body: '{}' });
    navigate(getLoginPath(), { replace: true });
  }

  function openChangePassword() {
    setPwdOpen(true);
  }

  return (
    <MediaStorageProvider>
    <div className="flex min-h-screen md:h-screen md:max-h-screen md:overflow-hidden">
      {menuOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/25 backdrop-blur-[2px] transition-opacity md:hidden"
          aria-label="关闭菜单"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[17rem] flex-col border-r border-slate-200/90 bg-gradient-to-b from-white via-white to-slate-50/90 shadow-xl shadow-slate-900/5 transition-transform duration-200 ease-out',
          'md:relative md:inset-auto md:z-0 md:h-screen md:max-h-screen md:shrink-0 md:translate-x-0 md:shadow-none',
          menuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        <div className="relative shrink-0 overflow-hidden border-b border-slate-200/70 bg-white/80 px-5 py-4">
          <div
            className="pointer-events-none absolute -right-6 -top-10 h-28 w-28 rounded-full bg-teal-400/10 blur-2xl"
            aria-hidden
          />
          <div className="relative flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-icon text-lg text-white shadow-md shadow-teal-500/25 ring-1 ring-teal-500/10">
              <i className="fa-solid fa-shrimp" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className="text-[15px] font-semibold leading-tight tracking-tight text-slate-800">{APP_BRAND_TITLE}</h1>
              <p className="mt-0.5 text-xs text-slate-500">信息后台</p>
            </div>
          </div>
        </div>

        <nav className="min-h-0 flex-1 space-y-0 overflow-y-auto overscroll-contain p-3 pt-2 text-[13px]">
          {APP_NAV_SECTIONS.map((section, idx) => (
            <div key={section.id} className={cn(idx > 0 && 'mt-3 border-t border-slate-200/80 pt-3')}>
              <p className="mb-1.5 px-3 text-[11px] font-semibold tracking-wide text-slate-400">{section.label}</p>
              <div className="space-y-0.5">
                {section.items
                  .filter((item) => !item.need || capabilities[item.need])
                  .map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end === true}
                      className={({ isActive }) =>
                        cn(
                          'flex min-h-[2.75rem] items-center gap-3 rounded-lg px-3 py-2.5 font-medium transition-colors duration-150 md:min-h-0',
                          isActive
                            ? 'bg-teal-50 text-teal-900 ring-1 ring-teal-200/70'
                            : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <i
                            className={cn(
                              'fa-fw w-5 text-center text-[15px]',
                              item.icon,
                              isActive ? 'text-teal-600' : 'text-slate-400'
                            )}
                            aria-hidden
                          />
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-slate-200/80 bg-white/70 p-3">
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
          >
            <i className="fa-solid fa-right-from-bracket text-xs opacity-60" aria-hidden />
            退出登录
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col md:overflow-y-auto">
        <header
          className="sticky top-0 z-30 flex min-h-14 shrink-0 items-center gap-3 border-b border-slate-200/70 bg-white/80 px-4 shadow-sm shadow-slate-900/[0.04] backdrop-blur-xl md:hidden"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-icon text-sm text-white shadow-md shadow-teal-900/25 ring-1 ring-white/10">
            <i className="fa-solid fa-shrimp" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold tracking-tight text-slate-900">{APP_BRAND_TITLE}</p>
            <p className="text-xs text-slate-400">信息后台</p>
          </div>
          {ok ? <HeaderAccountBar onChangePassword={openChangePassword} className="shrink-0 scale-90" /> : null}
        </header>

        {ok ? (
          <div
            className="hidden border-b border-slate-200/80 bg-white/75 px-6 py-2.5 shadow-sm shadow-slate-900/[0.03] backdrop-blur-xl md:block"
            style={{
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right))',
            }}
          >
            <div className="mx-auto flex max-w-screen-2xl justify-end">
              <HeaderAccountBar onChangePassword={openChangePassword} />
            </div>
          </div>
        ) : null}

        <main className="min-w-0 flex-1 px-3 pb-[max(7rem,calc(5.5rem+env(safe-area-inset-bottom)))] pt-4 sm:px-4 md:overflow-x-hidden md:px-4 md:py-5 md:pb-5 lg:px-5 lg:py-6 lg:pb-6">
          <div className="mx-auto w-full max-w-screen-2xl">
            {ok && siteScoped ? <SiteScopeBar compact className="mb-4 md:mb-5" /> : null}
            <Outlet key={siteScoped && currentSiteId > 0 ? currentSiteId : 'session'} />
          </div>
        </main>

        <MobileTabBar menuOpen={menuOpen} onOpenMenu={() => setMenuOpen(true)} />
      </div>

      <ChangePasswordModal
        open={pwdOpen}
        onClose={() => setPwdOpen(false)}
        onSuccess={() => void refresh()}
      />
    </div>
    </MediaStorageProvider>
  );
}
